<template>
  <div
    :class="`${variant}-tabs-wrapper`"
    :style="{ maxWidth: maxWidth }"
  >
    <div :class="`${variant}-tabs-container`">
      <div
        v-for="(tab, index) in filteredTabs"
        :key="tab.name"
        :title="tab.title"
        v-show="!tab.hidden"
        @click="handleClick(tab)"
        :class="{
          [`${variant}-tab-item`]: true,
          [`${variant}-selected`]: tab.selected,
          [`${variant}-disabled`]: tab.disabled,
          [`${variant}-warning`]: tab.warning,
          [`${variant}-error`]: tab.error,
          [`${variant}-blink`]: tab.warning || tab.error,
          [`${variant}-first`]: variant === 'group' && index === 0,
          [`${variant}-last`]: variant === 'group' && index === filteredTabs.length - 1,

        }"
      >
        {{ tab.name }}
      </div>
    </div>
  </div>
</template>

<script>
import { useStore } from "vuex"
import { computed, defineComponent, ref } from "vue"
export default defineComponent({
 name: "GenericTabs",
 components: {},
 props: {
  tabs: {
    type: Array,
    default: () => ([]),
  },
  variant: {
    type: String,
    default: 'default'
  },
  maxWidth: {
    type: String,
    default: '95%'
  }
 },
 emits: ['activeTab'],
setup(props, context) {
  const store = useStore();
  const selectedTabName = ref('');
  const filteredTabs = computed(() => props.tabs.filter(tab => !tab.hidden))
  const handleClick = (tab) => {
    if (selectedTabName.value !== tab.name && !tab.disabled) {
      context.emit('activeTab', tab);
      selectedTabName.value = tab.name;
    }
  }
  return {
    state: store.state,
    handleClick,
    filteredTabs
  }
},
})
</script>

<style scoped>

@keyframes blinker {
  50% {
    opacity: 0;
  }
}

/* VARIANT: DEFAULT */
.default-tabs-wrapper {
  position: relative;
  display: flex;
  justify-content: space-evenly;
  flex-wrap: wrap;
  width: 100%;
  margin: auto;
}
.default-tabs-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-evenly;
  font-family: var(--font-family-gothic);
  font-size: 12px;
  width: 100%;
}
.default-tab-item {
  cursor: pointer;
  text-align: center;
  margin: 0;
  padding: 0px 4px;
  border-left: 2px solid transparent;
  border-right: 2px solid transparent;
}
.default-tab-item:hover {
  text-shadow: 0px 0px 5px #979696;
  color: #dbffd0;
}
.default-selected {
  color: #eeffef;
  text-shadow: 0px 0px 5px var(--text-green);
  cursor: default;
  padding: 0px 4px;
  border-left: 2px solid #dbffd0;
  border-right: 2px solid #dbffd0;
  border-radius: 2px;
}
.default-disabled {
  color: gray;
  cursor: not-allowed;
}
.default-disabled:hover {
  color: rgb(197, 197, 197);
  text-shadow: 0px 0px 5px #979696;
  cursor: not-allowed;
}
.default-warning {
  color: var(--success-1);
  text-shadow: var(--text-shadow);
  border-color: var(--success-1);
}
.default-warning:hover {
  color: var(--success-0);
  text-shadow: var(--text-shadow);
}
.default-blink {
  animation: blinker 2s linear infinite;
}
/* VARIANT: CONTAINED */
.contained-tabs-wrapper {
  position: relative;
  display: flex;
  justify-content: space-evenly;
  flex-wrap: wrap;
  width: 100%;
  margin: 0px auto 4px;
  background-color: var(--sub-panel-bg);
  border: var(--border-light);
  border-radius: 3px;
}
.contained-tabs-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-evenly;
  font-family: var(--font-family-gothic);
  font-size: 14px;
  width: 100%;
}
.contained-tab-item {
  cursor: pointer;
  text-align: center;
  margin: 0;
  padding: 0px 4px;
}
.contained-tab-item:hover {
  text-shadow: 0px 0px 5px #979696;
  color: #dbffd0;
}
.contained-selected {
  color: #eeffef;
  text-shadow: 0px 0px 5px var(--text-green);
  cursor: default;
  padding: 0px 4px;
  border-radius: 2px;
}
.contained-disabled {
  color: gray;
  cursor: not-allowed;
}
.contained-disabled:hover {
  color: rgb(197, 197, 197);
  text-shadow: 0px 0px 5px #979696;
  cursor: not-allowed;
}
.contained-warning {
  color: var(--success-1);
  text-shadow: var(--text-shadow);
  border-color: var(--success-1);
}
.contained-warning:hover {
  color: var(--success-0);
  text-shadow: var(--text-shadow);
}
.contained-blink {
  animation: blinker 2s linear infinite;
}

/* VARIANT: GROUP */
.group-tabs-wrapper {
  position: relative;
  display: flex;
  justify-content: space-evenly;
  flex-wrap: wrap;
  width: 100%;
  margin: auto;
}
.group-tabs-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-evenly;
  font-family: var(--font-family-gothic);
  font-size: 14px;
  line-height: 16px;
  width: 100%;
  gap: 2px;
}
.group-tab-item {
  cursor: pointer;
  text-align: center;
  margin: 0;
  border: var(--border-light);
  padding: 0px 4px;
  flex-grow: 1;
}
.group-tab-item:hover {
  color: #dbffd0;
  border-color: var(--text-tagged);
}
.group-selected {
  color: #eeffef;
  cursor: default;
  padding: 0px 4px;
  background-color: #aeb4b0;
  background-color: #5e7957d5;
  background-color: rgba(133, 252, 90, 0.4);
  border-color: var(--text-tagged);
}
.group-disabled {
  color: gray;
  cursor: not-allowed;
}
.group-disabled:hover {
  color: rgb(197, 197, 197);
  text-shadow: 0px 0px 5px #979696;
  cursor: not-allowed;
}
.group-warning {
  color: var(--success-1);
  text-shadow: var(--text-shadow);
  border-color: var(--success-1);
  background-color: transparent;
}
.group-warning:hover {
  color: var(--success-0);
  text-shadow: var(--text-shadow);
}
.group-blink {
  animation: blinker 2s linear infinite;
}

.group-first {
  border-radius: 4px 0 0 4px;
}
.group-last {
  border-radius: 0 4px 4px 0;
}

</style>